import React from 'react';
import Icon from '../AppIcon';

const MediaGrid = ({
  images = [],
  onSelectImage,
  onDeleteImage,
  onCopyUrl,
  selectedUrl = null,
  loading = false,
}) => {
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="aspect-square rounded-lg bg-gray-200 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-400">
        <Icon name="Image" size={48} className="mb-4" />
        <p className="text-lg font-medium">No images uploaded yet</p>
        <p className="text-sm">Upload your first image to get started</p>
      </div>
    );
  }

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {images.map((image) => {
        const isSelected = selectedUrl === image.url;

        return (
          <div
            key={image.key}
            className={`group relative aspect-square rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
              isSelected
                ? 'border-primary ring-2 ring-primary ring-offset-2'
                : 'border-gray-200 hover:border-primary'
            }`}
            onClick={() => onSelectImage && onSelectImage(image.url)}
          >
            <img
              src={image.url}
              alt={image.key}
              className="w-full h-full object-cover"
              loading="lazy"
            />

            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                {onSelectImage && (
                  <button
                    className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectImage(image.url);
                    }}
                    title="Select image"
                  >
                    <Icon name="Check" size={16} className="text-primary" />
                  </button>
                )}
                {onCopyUrl && (
                  <button
                    className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCopyUrl(image.url);
                    }}
                    title="Copy URL"
                  >
                    <Icon name="Copy" size={16} className="text-gray-700" />
                  </button>
                )}
                {onDeleteImage && (
                  <button
                    className="p-2 bg-white rounded-full hover:bg-red-50 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Are you sure you want to delete this image?')) {
                        onDeleteImage(image.key);
                      }
                    }}
                    title="Delete image"
                  >
                    <Icon name="Trash2" size={16} className="text-error" />
                  </button>
                )}
              </div>
            </div>

            {/* Image info */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <p className="text-white text-xs truncate">{image.key}</p>
              <p className="text-white text-xs">
                {formatFileSize(image.size)} • {formatDate(image.lastModified)}
              </p>
            </div>

            {/* Selected indicator */}
            {isSelected && (
              <div className="absolute top-2 right-2 bg-primary rounded-full p-1">
                <Icon name="Check" size={16} className="text-white" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MediaGrid;
